<template>
  <div class="join-form">
    <div class="join-form--left">
      <div class="join-form--left--benefits">
        <div class="join-form--left--top">
          <h2>Advocacy & Policy</h2>
          <p>
            Working towards an effective health system is at the heart of what
            we do at the AMA. That's why each of our campaigns are designed to
            drive results on what matters for both our members and the health
            needs of Australians.
          </p>
        </div>
        <div class="join-form--left--image">
          <img
            src="/modules/amacustom/ama/ama_dashboard/images/join-advocacy-policy.svg"
          />
        </div>
      </div>
    </div>
    <div class="join-form--right">
      <div class="join-form--step step-six">
        <div class="step-legend">
          <h2>Review information</h2>
          <step-circles step="4" />
        </div>
        <div class="step-intro">
          Please take a moment to confirm your details below are correct.
        </div>
        <div class="step-form--scroll--wrapper">
          <div @scroll="checkTooltip" class="step-form step-form--scroll">
            <price-box
              v-if="data.stage != 'student'"
              :options="options"
              :form="form"
              :discount="discount"
              :prices="prices"
              :cpd="data.cpd_sub"
              formType="join"
            />
            <form
              @submit.prevent="doSubmit"
              @keypress.enter.prevent
              class="nice-form"
            >
              <custom-field
                label="Title"
                type="select"
                :rules="{
                  required: { message: 'Please select your title.' },
                }"
                v-model="data.prefix"
              >
                <template v-slot:options>
                  <option value="" disabled="disabled"></option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Miss">Miss</option>
                  <option value="Dr">Dr</option>
                  <option value="Prof">Professor</option>
                  <option value="A/Prof">Associate Professor</option>
                  <option value="Mx">Mx</option>
                </template>
              </custom-field>
              <custom-field
                label="First name"
                maxlength="20"
                :rules="{
                  required: { message: 'Please add your first name.' },
                }"
                v-model="data.name"
              />
              <custom-field
                label="Last name"
                maxlength="20"
                :rules="{
                  required: { message: 'Please add your last name.' },
                }"
                v-model="data.lastname"
              />
              <div class="d-flex flex-wrap">
                <label class="top-label" :class="{ filled: dobSelected }"
                  >Date of birth</label
                >
                <div class="col-12 col-md-4 pe-md-2">
                  <div class="form-group">
                    <select
                      id="day"
                      class="form-select data-hj-allow"
                      @focus="dateInputFocus = true"
                      @blur="dateInputFocus = false"
                      required="true"
                      v-model="data.day"
                    >
                      <option value="" disabled></option>
                      <option v-for="i in 31" :key="`day-${i}`">{{ i }}</option>
                    </select>
                    <label for="day">Day</label>
                  </div>
                </div>
                <div class="col-12 col-md-4 px-md-2">
                  <div class="form-group">
                    <select
                      id="month"
                      class="form-select data-hj-allow"
                      @focus="dateInputFocus = true"
                      @blur="dateInputFocus = false"
                      required="true"
                      v-model="data.month"
                    >
                      <option value="" disabled></option>
                      <option value="01">January</option>
                      <option value="02">February</option>
                      <option value="03">March</option>
                      <option value="04">April</option>
                      <option value="05">May</option>
                      <option value="06">June</option>
                      <option value="07">July</option>
                      <option value="08">August</option>
                      <option value="09">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                    </select>
                    <label for="month">Month</label>
                  </div>
                </div>
                <div class="col-12 col-md-4 ps-md-2">
                  <div class="form-group">
                    <select
                      id="year"
                      class="form-select data-hj-allow"
                      @focus="dateInputFocus = true"
                      @blur="dateInputFocus = false"
                      required="true"
                      v-model="data.year"
                    >
                      <option value="" disabled></option>
                      <option v-for="i in 72" :key="`year-${i}`">
                        {{ lastYear - i }}
                      </option>
                    </select>
                    <label for="year">Year</label>
                  </div>
                </div>
              </div>
              <custom-field
                label="Gender"
                type="select"
                :rules="{
                  required: {},
                }"
                v-model="data.gender"
              >
                <template v-slot:options>
                  <option value="" disabled>Select</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="X">Non-binary</option>
                  <option value="I">Intersex</option>
                  <option value="Z">I prefer not to say</option>
                </template>
              </custom-field>
              <div class="form-group">
                <input
                  class="form-control data-hj-allow"
                  required="true"
                  type="text"
                  v-model="data.addressString"
                />
                <label for="address">Residential address</label>
              </div>
              <custom-field
                label="Mobile number"
                maxlength="25"
                type="tel"
                :rules="{
                  required: { message: 'Please add your mobile.' },
                  regexr: {
                    pattern: /^\d{10}$/,
                    message: 'The mobile number is invalid',
                  },
                }"
                v-model="data.phone"
              />
              <div
                v-if="data.stage != 'student' && !hideEmployment"
                class="form-group"
              >
                <select
                  class="form-select data-hj-allow"
                  required="true"
                  v-model="data.employment"
                  @change="checkMembership"
                >
                  <option value="" disabled=""></option>
                  <option
                    v-for="(e, k) in employment"
                    :value="k"
                    :key="`e-${k}`"
                  >
                    {{ e.label }}
                  </option>
                </select>
                <label for="employment">Concession rates for membership</label>
              </div>
              <div class="form-group ama-partner-rate">
                <input
                  type="checkbox"
                  class="d-none"
                  id="partner-rate"
                  :checked="Boolean(data.partner_rate)"
                  @change="togglePartnerRate"
                />
                <label for="partner-rate">
                  <span v-if="data.partner_rate">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                    >
                      <path
                        fill="currentColor"
                        d="M400 480H48c-26.51 0-48-21.49-48-48V80c0-26.51 21.49-48 48-48h352c26.51 0 48 21.49 48 48v352c0 26.51-21.49 48-48 48zm-204.686-98.059l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.248-16.379-6.249-22.628 0L184 302.745l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.25 16.379 6.25 22.628.001z"
                      ></path>
                    </svg>
                  </span>
                  <span v-else>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                    >
                      <path
                        fill="currentColor"
                        d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zm-6 400H54c-3.3 0-6-2.7-6-6V86c0-3.3 2.7-6 6-6h340c3.3 0 6 2.7 6 6v340c0 3.3-2.7 6-6 6z"
                      ></path>
                    </svg>
                  </span>
                  <strong
                    >Do you have a partner who is also a member of the
                    AMA?</strong
                  >
                </label>
              </div>
              <div v-if="data.state == 'NSW'" class="form-group">
                <custom-field
                v-if="data.partner_rate"
                maxlength="30"
                label="Partner name"
                :rules="{ required: {} }"
                v-model="data.paterner_ama"
                style="margin-top: 15px;"
                /> 
              </div>
              <div v-if="data.state == 'NSW'" class="form-group ama-partner-rate">
                <input type="checkbox" class="d-none" id="ama-member" v-model="data.ama_member" value="1"/>
                <label for="ama-member">
                  <span v-if="data.ama_member">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" ><path fill="currentColor" d="M400 480H48c-26.51 0-48-21.49-48-48V80c0-26.51 21.49-48 48-48h352c26.51 0 48 21.49 48 48v352c0 26.51-21.49 48-48 48zm-204.686-98.059l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.248-16.379-6.249-22.628 0L184 302.745l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.25 16.379 6.25 22.628.001z"></path></svg>
                  </span>
                  <span v-else>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" ><path fill="currentColor" d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zm-6 400H54c-3.3 0-6-2.7-6-6V86c0-3.3 2.7-6 6-6h340c3.3 0 6 2.7 6 6v340c0 3.3-2.7 6-6 6z"></path></svg>
                  </span>
                  <strong>Were you referred by an existing AMA member?</strong>
                </label>
              </div>
              <custom-field
                v-if="data.ama_member"
                maxlength="30"
                label="Member name"
                :rules="{ required: {} }"
                v-model="data.ama_referee"
                style="margin-top: 15px;"
               />
              <div class="form-group">
                <select id="reason" class="form-select data-hj-allow" v-model="data.reason" :class="{ignore: !data.reason}">
                  <option value="" disabled=""></option>
                  <option value="FLLA">First-line legal assistance</option>
                  <option value="LMSF">
                    List of medical services and fees
                  </option>
                  <option value="ESAW">Events, seminars and workshops</option>
                  <option value="ADIN">Advocacy and Influence</option>
                  <option value="CSCS">
                    Coaching services and career solutions
                  </option>
                  <option value="CTAL">CPD tracking and learning</option>
                  <option value="MAOF">Medical Journal of Australia</option>
                  <option value="ACPB">
                    Access commercial partner benefits
                  </option>
                  <option value="KPMA">
                    Join the peak medical association
                  </option>
                  <option value="WIRS">
                    Access workplace and industrial relations support
                  </option>
                </select>
                <label for="reason">Primary reason for joining</label>
              </div>
              <div v-if="memberships.length > 1 && data.promoteCode !== 'AMANSW25'" class="form-group">
                <select
                  id="membership"
                  class="form-select data-hj-allow"
                  v-model="data.membership"
                  required="true"
                  :disabled="!data.employment"
                >
                  <option value="" disabled=""></option>
                  <option
                    v-for="mo in memberships"
                    :value="mo.key"
                    :key="mo.key"
                  >
                    {{ mo.label }}
                  </option>
                </select>
                <label for="membership">Membership type</label>
              </div>
              <div v-if="data.state == 'NSW' && data.stage == 'dit' && data.promoteCode !== 'AMANSW25'" class="form-group ama-partner-rate">
                <input type="checkbox" class="d-none" id="dit-intern" v-model="data.dit_intern" value="1"/>
                <label for="dit-intern">
                  <span v-if="data.dit_intern">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                        <path
                          fill="currentColor"
                          d="M400 480H48c-26.51 0-48-21.49-48-48V80c0-26.51 21.49-48 48-48h352c26.51 0 48 21.49 48 48v352c0 26.51-21.49 48-48 48zm-204.686-98.059l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.248-16.379-6.249-22.628 0L184 302.745l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.25 16.379 6.25 22.628.001z"
                        ></path>
                      </svg>
                  </span>
                    <span v-else>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                        <path
                          fill="currentColor"
                          d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zm-6 400H54c-3.3 0-6-2.7-6-6V86c0-3.3 2.7-6 6-6h340c3.3 0 6 2.7 6 6v340c0 3.3-2.7 6-6 6z"
                        ></path>
                      </svg>
                    </span>
                    <strong>Are you a Part-time Dortor-in-training?</strong>
                </label>
              </div>
              <div v-if="data.stage != 'student'" class="form-group">
                <select
                  class="form-select data-hj-allow"
                  required="true"
                  v-model="data.speciality"
                >
                  <option
                    v-for="(s, key) in options.crafts"
                    :value="key"
                    :key="`crafts-${key}`"
                  >
                    {{ s }}
                  </option>
                </select>
                <label for="speciality">Primary speciality/practice</label>
              </div>
              <div v-if="data.state == 'NSW' && data.stage != 'student'" class="form-group">
                <select class="form-select data-hj-allow" required="true" v-model="data.emp_as">
                  <option v-for="eo, key in options.employAs" :key="key" :value="key">{{ eo }}</option>
                </select>
                <label for="employAs">Employed As</label>
              </div>
              <div v-if="data.state == 'NSW' && data.stage != 'student'" class="form-group">
                <select class="form-select data-hj-allow" required="true" v-model="data.hospitalEmp">
                  <option v-for="h in options.employHos" :key="h.id" :value="h.id">{{ h.label }}</option>
                </select>
                <label for="employHospital">Hospital Employment</label>
              </div>
              <p v-if="data.stage != 'student'">Practice groups</p>
              <div
                v-if="data.stage != 'student'"
                class="speciality-option--wrapper mb-3"
              >
                <div
                  v-for="(label, key) in options.practices"
                  :key="`so-${key}`"
                  @click.prevent="setPractices(key)"
                  class="speciality-option--item"
                  :class="{ active: data.practice_groups.includes(key) }"
                >
                  <!--<i class="fal fa-check"></i>-->
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check fa-check"><path d="M20 6 9 17l-5-5"/></svg>
                  {{ label }}
                </div>
              </div>
              <p v-if="data.stage != 'student'">Communication preferences</p>
              <div
                v-if="data.stage != 'student'"
                class="speciality-option--wrapper mb-3"
                style="grid-template-columns: repeat(4, 1fr);"
              >
                <div
                  v-for="(label, key) in options.saCommunication"
                  :key="`so-${key}`"
                  @click.prevent="setSACommunication(key)"
                  class="speciality-option--item"
                  :class="{ active: data.sa_communication.includes(key) }"
                >
                  <!--<i class="fal fa-check"></i>-->
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check fa-check"><path d="M20 6 9 17l-5-5"/></svg>
                  {{ label }}
                </div>
              </div>
              <p v-if="data.stage != 'student' && data.state == 'SA'">medicSA</p>
              <div
                v-if="data.stage != 'student' && data.state == 'SA'"
                class="speciality-option--wrapper mb-3"
                style="grid-template-columns: repeat(2, 1fr);"
              >
                <div
                  v-for="(label, key) in options.saMedicSA"
                  :key="`so-${key}`"
                  @click.prevent="setMedicSA(key)"
                  class="speciality-option--item"
                  :class="{ active: data.sa_medicSA.includes(key) }"
                >
                  <!--<i class="fal fa-check"></i>-->
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check fa-check"><path d="M20 6 9 17l-5-5"/></svg>
                  {{ label }}
                </div>
              </div>
              <div v-if="data.stage !== 'student' && data.state == 'NSW'">
                <p>Professional group</p>
                <div class="speciality-option--wrapper mb-3 speciality-three">
                  <div v-for="x, key in options.profGroup" :key="key" @click.prevent="setGroups(key)"
                    class="speciality-option--item" :class="{'active': data.professional_groups.includes(key)}">
                    <!--<i class="fal fa-check"></i>-->
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check fa-check"><path d="M20 6 9 17l-5-5"/></svg>
                    {{ x }}
                  </div>
                </div>
              </div>  
              <div v-if="data.stage !== 'student' && data.state == 'QLD'">
                <p>Workplace Issue</p>
                <div class="speciality-option--wrapper mb-3 speciality-singl">
                  <div v-for="t in workplaceChoice" :key="t" @click.prevent="setWorkpace(t)"
                    class="speciality-option--item" :class="{'active': data.workpaceSetting == t}">
                    <!--<i class="fal fa-check"></i>-->
                    {{ t }}
                  </div>
                </div>
              </div>
              <div v-if="data.stage !== 'student' && data.state == 'TAS' || data.state == 'NT'">
                <p>ASMOF</p>
                <div class="speciality-option--wrapper mb-3 speciality-singl">
                  <div v-for="m in asmofBox" :key="m" @click.prevent="setasmofBox(m)"
                    class="speciality-option--item" :class="{'active': data.asmof == m}">
                    <!--<i class="fal fa-check"></i>-->
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check fa-check"><path d="M20 6 9 17l-5-5"/></svg>
                    {{ m }}
                  </div>
                </div>
              </div>
              <custom-field
                label="Email address"
                type="email"
                maxlength="50"
                :rules="{
                  required: {
                    message: 'An email address must contain a single @.',
                  },
                  email: {},
                }"
                v-model="data.email"
              />
              <custom-field
                label="Password"
                :type="passwordInput ? 'text' : 'password'"
                :display-errors="false"
                :rules="{
                  required: true,
                  regexr: { pattern: passwordPattern },
                }"
                v-model="data.password"
              >
                <template v-slot:suffix>
                  <a
                    class="field-suffix"
                    :class="{ active: passwordInput }"
                    href="#"
                    @click.prevent="toggleInput('password')"
                  >
                    <i class="fa fa-eye"></i>
                  </a>
                </template>
              </custom-field>
              <div class="form-password-indicator">
                <div :class="{ active: passwordCheck.lowercase }">
                  <i class="fal fa-check"></i>
                  <i class="fas fa-circle"></i>
                  <span>One lowercase character</span>
                </div>
                <div :class="{ active: passwordCheck.eigth }">
                  <i class="fal fa-check"></i>
                  <i class="fas fa-circle"></i>
                  <span>8 characters minimun</span>
                </div>
                <div :class="{ active: passwordCheck.number }">
                  <i class="fal fa-check"></i>
                  <i class="fas fa-circle"></i>
                  <span>One number</span>
                </div>
              </div>
              <div v-if="data.stage == 'student'">
                <div class="form-group">
                  <select
                    id="university"
                    class="form-select data-hj-allow"
                    v-model="data.university"
                    required=""
                  >
                    <option value="" disabled=""></option>
                    <option value="ANU">Australian National University</option>
                    <option value="BOND">Bond University</option>
                    <option value="CURTIN">Curtin University</option>
                    <option value="DEAKIN">Deakin University</option>
                    <option value="FLINDERS">Flinders University</option>
                    <option value="GRIFFITH">Griffith University</option>
                    <option value="JCU">James Cook University</option>
                    <option value="MONASH">Monash University</option>
                    <option value="MURDOCH">Murdoch University</option>
                    <option value="MQ">Macquarie University</option>
                    <option value="ADELAIDE">The University of Adelaide</option>
                    <option value="MELBOURNE">
                      The University of Melbourne
                    </option>
                    <option value="UNE">The University of New England</option>
                    <option value="UNSW">
                      The University of New South Wales
                    </option>
                    <option value="NEWCASTLE">
                      The University of Newcastle
                    </option>
                    <option value="UNDF">
                      The University of Notre Dame (Fremantle)
                    </option>
                    <option value="UNDS">
                      The University of Notre Dame (Sydney)
                    </option>
                    <option value="UQ">The University of Queensland</option>
                    <option value="USYD">The University of Sydney</option>
                    <option value="UTAS">The University of Tasmania</option>
                    <option value="UWA">
                      The University of Western Australia
                    </option>
                    <option value="WSYD">
                      The University of Western Sydney
                    </option>
                    <option value="WOLLON">The University of Wollongong</option>
                  </select>
                  <label for="university">University/School</label>
                </div>
                <div class="form-group">
                  <select
                    id="year"
                    class="form-select data-hj-allow"
                    v-model="data.current_year"
                    required=""
                  >
                    <option value="" disabled=""></option>
                    <option value="F1S1">First year</option>
                    <option value="F2S1">Second year</option>
                    <option value="F3S1">Third year</option>
                    <option value="F4S1">Fourth year</option>
                    <option value="F5S1">Fifth year</option>
                    <option value="F6S1">Sixth year</option>
                  </select>
                  <label for="year">Current year of study</label>
                </div>
                <div class="form-group">
                  <input
                    id="start_year"
                    class="form-control data-hj-allow"
                    required=""
                    type="number"
                    data-lpignore="true"
                    v-model="data.start_year"
                  />
                  <label for="start_year">Commencement year</label>
                </div>
                <div class="form-group">
                  <input
                    id="end_year"
                    class="form-control data-hj-allow"
                    required
                    type="number"
                    data-lpignore="true"
                    v-model="data.end_year"
                  />
                  <label for="end_year">Expected graduation year</label>
                </div>
                <div class="form-group">
                  <select
                    id="interest"
                    class="form-select data-hj-allow"
                    v-model="data.interest"
                    required=""
                  >
                    <option value="" disabled=""></option>
                    <option
                      v-for="(label, key) in options.crafts"
                      :key="`i-${key}`"
                      :value="key"
                    >
                      {{ label }}
                    </option>
                  </select>
                  <label for="interest">Post graduate field of interest</label>
                </div>
              </div>
              <div v-if="loading" class="step-footer">
                <button
                  type="button"
                  class="button button-outline-blue button-nm"
                  disabled="disabled"
                >
                  Back
                </button>
                <button
                  type="button"
                  class="button button-blue button-nm"
                  disabled="disabled"
                >
                  <!---<i class="fas fa-spinner fa-spin"></i>-->
                  <span class="loader"></span>
                </button>
              </div>
              <div v-else class="step-footer">
                <button
                  type="button"
                  @click.prevent="$emit('update:step', 4)"
                  class="button button-outline-blue button-nm"
                >
                  Back
                </button>
                <button
                  v-if="data.stage == 'student'"
                  type="submit"
                  class="button button-blue button-nm"
                >
                  Join
                </button>
                <button
                  v-else-if="data.status == 'NSW' && data.payment === 'A'"
                  type="submit"
                  class=""
                  disabled
                >
                  Continue to payment
                </button>
                <button
                  v-else
                  type="submit"
                  class="button button-blue button-nm"
                  :disabled="invalid.business_address"
                >
                  Continue to payment
                </button>
              </div>
            </form>
          </div>
          <div v-if="tooltip" class="scroll-tooltip">
            <div class="scroll-tooltip--content">
              <ScrollIcon />
              <span>Scroll down for more details</span>
            </div>
            <div class="scroll-tooltip--arrow"></div>
          </div>
        </div>
        
      </div>
    </div>
  </div>
</template>
<script>
  import stepCircles from "../stepCircles"
  import PriceBox from "./PriceBox"
  import customField from "../forms/customField"
  import { ScrollIcon } from "../icons"
  export default {
    name: "step-five",
    props: ["step", "form", "options", "prices", "discount", "loading"],
    components: {
      stepCircles,
      ScrollIcon,
      PriceBox,
      customField,
    },
    data() {
      return {
        passwordPattern:
          "^(?=.*[A-Za-z])(?=.*\\d)[A-Za-z\\d*.!@$#%'\"~^&(){}\\[\\]:;<>,.?\\/~_+-=|\\]]{8,}$",
        passwordInput: false,
        tooltip: true,
        dateInputFocus: false,
        lastYear: new Date().getFullYear() - 15,
        data: {
          practice_groups: [],
          professional_groups: [],
          privacy: []
        },
        invalid: {
          business_address: false,
        },
        workplaceChoice: [ 'Yes', 'No'],
        asmofBox: [ 'Yes', 'No'],
      }
    },
    watch: {
      form: {
        immediate: true,
        handler(newValue) {
          if (!newValue) {
            return;
          }

          this.data = {
            ...this.data,
            ...newValue,
          };
        },
      },
    },
    computed: {
      passwordCheck() {
        let passwordCheck = {}
        const password = this.data.password || ""
        passwordCheck.eigth = password.length > 7
        passwordCheck.number = !!/[0-9]+/.test(password)
        passwordCheck.lowercase = !!/[a-z]+/.test(password)
        return passwordCheck
      },
      dobSelected() {
        if (this.data.day && this.data.month && this.data.year) {
          return true
        }

        return this.dateInputFocus
      },
      employment() {
        const { stage } = this.data;
        const state = this.data.state;
        if (stage && stage != 'student') {
          return this.options.memberships[state][stage]['arrangement'];
        }
        return []
      },
      hideEmployment() {
        return Object.keys(this.employment).length == 1
      },
      memberships() {
        const { stage, employment, state} = this.data;
        //const state = this.data.state;
        if (stage && stage != 'student' && employment) {
          const memberships = this.options.memberships[state];
          return memberships[stage]['arrangement'][employment]['options'];
        }

        return []
      },
    },
    methods: {
      toggleInput() {
        this.passwordInput = !this.passwordInput
      },
      checkMembership() {
        if (this.memberships && this.memberships.length == 1) {
          this.data.membership = this.memberships[0]["key"]
        }
      },
      togglePartnerRate(event) {
        const checked = event.target.checked;
        const value = checked ? 1 : 0;
        const nextForm = {
          ...this.form,
          partner_rate: value,
        };

        console.log('partner_rate toggle:', { checked, value, nextForm });
        this.$set(this.data, 'partner_rate', value);
        this.$emit('update:form', nextForm);
      },
      setPractices(key) {
        if (this.data.practice_groups.includes(key)) {
          this.data.practice_groups = this.data.practice_groups.filter(
            (i) => i !== key
          )
        } else {
          this.data.practice_groups = [...this.data.practice_groups, key]
        }
      },
      setSACommunication(key) {
        if (this.data.sa_communication.includes(key)) {
          this.data.sa_communication = this.data.sa_communication.filter(
            (i) => i !== key
          )
        } else {
          this.data.sa_communication = [...this.data.sa_communication, key]
        }
      },
      setMedicSA(key) {
        if (this.data.sa_medicSA.includes(key)) {
          this.data.sa_medicSA = this.data.sa_medicSA.filter(
            (i) => i !== key
          )
        } else {
          this.data.sa_medicSA = [...this.data.sa_medicSA, key]
        }
      },
      setGroups(id) {
        if (this.data.professional_groups.includes(id)) {
          this.data.professional_groups = this.data.professional_groups.filter(i => i !== id)
        }
        else {
          this.data.professional_groups = [...this.data.professional_groups, id];
        }
      },
      setWorkpace(e) {
        //console.log('workpaceIssue:', this.workpaceIssue)
        if(this.data.workpaceSetting != e){
          this.data.workpaceSetting = e
        } 
      },
      setasmofBox(e) {
        //console.log('workpaceIssue:', this.workpaceIssue)
        if(this.data.workpaceSetting != e){
          this.data.workpaceSetting = e
        } 
      },
      getAddressData(fields) {
        var address = {
          address_1: "",
          address_2: "",
          address_3: "",
          city: "",
          state: "",
          postcode: "",
        }

        if (fields.street_number && fields.route) {
          address.address_1 = fields.street_number + " " + fields.route
        } else if (fields.route) {
          address.address_1 = fields.route
        }

        if (fields.locality) {
          address.address_2 = fields.locality
        }

        if (fields.administrative_area_level_2) {
          address.city = fields.administrative_area_level_2
        }

        if (fields.administrative_area_level_1) {
          address.state = fields.administrative_area_level_1
        }

        if (fields.postal_code) {
          address.postcode = fields.postal_code
        }

        this.data.business_address = address
        this.data.businessAddress =
          this.$options.filters.addressDisplay(address)
        this.invalid.business_address = !fields.street_number
      },
      checkTooltip(e) {
        const container = e.target
        const totalScroll = container.offsetHeight + container.scrollTop + 30
        this.tooltip = totalScroll < container.scrollHeight
      },
      doSubmit() {
        let failed = false
        for (var i in this.$children) {
          const child = this.$children[i]
          if (child.onFocus) {
            const errors = child.onBlur()
            if (errors.length) {
              failed = true
            }
          }
        }

        if (failed) return

        // if (this.data.employment != "fullTime") {
        //   this.data.partner_rate = 0
        // }

        if (this.data.stage == "student") {
          this.$emit("process")
        } else {
          this.$emit("update:step", 6)
        }
      },
    },
    mounted() {
      this.data = this.form;
      //console.log(this.data);
      if(this.data.promoteCode == 'AMANSW25'){
        this.data.payment = 'A';
      }
    }
  }
</script>
